import {IPortalseite} from "./Interfaces";
import Veraportal from "./Veraportal";
import Modal from "./Modal";
import VeraApi from "./VeraApi";
import Utilities from "./Utilities";

/**
 * Repräsentiert die Seite zur Verwaltung des schulweiten Testmodus oder der Testheftvariante.
 * Erweitert die Basisfunktionalität von Veraportal und implementiert IPortalseite.
 */
export default class SchuleVerwalten extends Veraportal implements IPortalseite {
    /**
     * Wird beim Laden der Seite asynchron ausgeführt.
     * Initialisiert die UI-Elemente und Event-Listener für die schulweite Testentscheidung.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt, wenn die Seite erfolgreich geladen wurde.
     */
    override async onSeiteLoad(): Promise<boolean> {
        if (this.sessionvariablen.projekt.weiche === 'vera8') {
            // Setzt den initialen Wert des Testmodus-Dropdowns basierend auf dem Schulstatus
            if (this.schulstatus.is_cbt_schule_mathematik === 1)
                (document.querySelector(`#testmodus-select`) as HTMLSelectElement).value = 'cbt';
            else if (this.schulstatus.is_cbt_schule_mathematik === 0)
                (document.querySelector(`#testmodus-select`) as HTMLSelectElement).value = 'pp';
            else
                (document.querySelector(`#testmodus-select`) as HTMLSelectElement).value = '-';

            // Hinweis nur einblenden, wenn bereits ein Testmodus ausgewählt wurde
            if (this.schulstatus.is_cbt_schule_mathematik === null)
                document.querySelector(`#hinweis-testmodus-testheftauswahl`)?.classList.add('hidden');
            else
                document.querySelector(`#hinweis-testmodus-testheftauswahl`)?.classList.remove('hidden');

            // Initialisiert das TomSelect-Dropdown für den Testmodus
            // @ts-ignore
            new TomSelect(`#testmodus-select`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                onChange: (value: string) => {
                    let submitButton = document.querySelector(`#testmodus-speichern`) as HTMLButtonElement;
                    // Aktiviert oder deaktiviert den Speichern-Button basierend auf der Auswahl
                    if (value !== '-')
                        submitButton.disabled = false;
                    else
                        submitButton.disabled = true;
                }
            });

            // Fügt einen Event-Listener für den Speichern-Button hinzu
            document.querySelector(`button#testmodus-speichern`)
                ?.addEventListener('click', async () => {
                    try {
                        // Erstellt und zeigt ein Bestätigungsmodal an
                        const testmodusModal = new Modal(this);
                        const modalantwort = await testmodusModal.show({
                            title: `Möchten Sie den Testmodus der gesamten Schule wirklich ändern?`,
                            body: `
                                <p>
                                    Wenn Sie den Testmodus der Schule ändern, werden alle Gruppen in diesem Modus (CBT / PP) getestet.<br/>
                                    Dies betrifft sämtliche Gruppen in allen Fächern, für die bisher noch keine Ergebnisse im Portal hinterlegt wurden.
                                </p>
                                <p>
                                    Um fortzufahren, geben Sie im Bestätigungsfeld <i>"OK"</i> ein.
                                </p>
                            `,
                            hasSubmit: true,
                            hasCancel: true,
                            hasBestaetigung: true,
                            width: 600,
                            cancelText: 'Abbrechen',
                            submitText: 'Testmodus ändern'
                        });

                        if (modalantwort !== true)
                            return;

                        // Holt den ausgewählten Moduswert aus dem Dropdown
                        const modusValue: string = (document.querySelector(`#testmodus-select`) as HTMLSelectElement)?.value ?? null;
                        // Bricht ab, wenn der Moduswert ungültig ist
                        if (['pp', 'cbt'].includes(modusValue) === false)
                            return;

                        // Sendet die Modusänderung an die API
                        const api = VeraApi.getInstance(this);
                        const testmodusAendernResponse = await api.postTestmodus(modusValue);

                        // Lädt die Seite bei Erfolg neu
                        if (testmodusAendernResponse === true) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                    } catch (e) {
                        Utilities.errorToSentry(e);
                    }
                });
        }

        if (this.sessionvariablen.projekt.weiche === 'vera5') {
            const mathematik = this.schulstatus.testheft_schwierigkeit_mathematik ?? null;
            const deutsch = this.schulstatus.testheft_schwierigkeit_deutsch ?? null;
            const testheftvarianteGespeichert = mathematik !== null || deutsch !== null;
            const testheftvarianteSelect = document.querySelector(`#testheftvariante-select`) as HTMLSelectElement;
            const submitButton = document.querySelector(`#testheftvariante-speichern`) as HTMLButtonElement;

            if (mathematik === deutsch && (mathematik === 1 || mathematik === 2))
                testheftvarianteSelect.value = mathematik.toString();
            else
                testheftvarianteSelect.value = '-';

            testheftvarianteSelect.disabled = testheftvarianteGespeichert;

            // @ts-ignore
            new TomSelect(`#testheftvariante-select`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                onChange: (value: string) => {
                    submitButton.disabled = testheftvarianteGespeichert || value === '-';
                }
            });

            document.querySelector(`button#testheftvariante-speichern`)
                ?.addEventListener('click', async () => {
                    try {
                        const testheftvarianteModal = new Modal(this);
                        const modalantwort = await testheftvarianteModal.show({
                            title: `Möchten Sie diese Testheftvariante verbindlich festlegen?`,
                            body: `
                                <p>
                                    Die ausgewählte Testheftvariante gilt schulweit für Deutsch und Mathematik und kann anschließend nicht mehr geändert werden.
                                </p>
                                <p>
                                    Um fortzufahren, geben Sie im Bestätigungsfeld <i>"OK"</i> ein.
                                </p>
                            `,
                            hasSubmit: true,
                            hasCancel: true,
                            hasBestaetigung: true,
                            width: 600,
                            cancelText: 'Abbrechen',
                            submitText: 'Testheftvariante<br/>speichern'
                        });

                        if (modalantwort !== true)
                            return;

                        const schwierigkeit = Number(testheftvarianteSelect.value);
                        if ([1, 2].includes(schwierigkeit) === false)
                            return;

                        const api = VeraApi.getInstance(this);
                        const testheftvarianteResponse = await api.postTestheftvariante(schwierigkeit);
                        if (testheftvarianteResponse === true) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                    } catch (e) {
                        Utilities.errorToSentry(e);
                    }
                });
        }

        return true;
    }

    /**
     * Wird asynchron ausgeführt, nachdem die Seite vollständig geladen und verarbeitet wurde.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt.
     */
    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    /**
     * Wird asynchron ausgeführt, bevor die Seite verlassen wird.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt.
     */
    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }
}
